import React, { useEffect, useState, useRef } from 'react';
import { XIcon, CheckCircleIcon, AlertCircleIcon, LoaderIcon } from 'lucide-react';
interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const SubscribeModal: React.FC<SubscribeModalProps> = ({
  isOpen,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  // Reset form state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setSubmitStatus(null);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setSubmitStatus({
        success: false,
        message: 'Please enter your email address.'
      });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const formData = new FormData();
      formData.append('email_address', email);
      formData.append('tags', 'subscribe'); // Add the "subscribe" tag
      const response = await fetch('https://app.kit.com/forms/8424557/subscriptions', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer blo_gIPpz-EVmBjL0RaPcA'
        }
      });
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: "Thank you for subscribing! You will receive a confirmation email shortly. If you haven't received it within a few minutes, check your spam folder and be sure to move our emails to your primary folder so you receive them!"
        });
        setEmail('');
      } else {
        const errorData = await response.json().catch(() => null);
        if (errorData && errorData.error === 'Member Exists') {
          setSubmitStatus({
            success: true,
            message: "You're already subscribed! Thank you for your continued support."
          });
        } else {
          throw new Error('Submission failed');
        }
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'There was an error submitting your subscription. Please try again.'
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-756">
      <div ref={modalRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-757">
        <div className="flex justify-end p-4" data-id="element-758">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-759">
            <XIcon className="w-6 h-6" data-id="element-760" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-2" data-id="element-761">
          <div className="flex justify-center mb-8" data-id="element-762">
            <img src="/Untitled_design_%2886%29.png" alt="Creator Boost Logo" className="h-24" data-id="element-763" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-764">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-gray-600 text-center mb-6" data-id="element-765">
            Subscribe to learn the AI strategies other creators are using to
            stay ahead
          </p>
          <form className="space-y-4" onSubmit={handleSubmit} data-id="element-766">
            <div data-id="element-767">
              <input type="email" name="email_address" placeholder="Your email address" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-768" />
            </div>
            {submitStatus && <div className={`p-4 rounded-lg ${submitStatus.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-769">
                <div className="flex items-start" data-id="element-770">
                  {submitStatus.success ? <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" data-id="element-771" /> : <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-red-500" data-id="element-772" />}
                  <p className="text-sm" data-id="element-773">{submitStatus.message}</p>
                </div>
              </div>}
            <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-774">
              {isSubmitting ? <>
                  <LoaderIcon className="w-5 h-5 mr-2 animate-spin" data-id="element-775" />
                  <span data-id="element-776">Subscribing...</span>
                </> : <span data-id="element-777">Subscribe</span>}
            </button>
            <p className="text-xs text-gray-500 text-center mt-4" data-id="element-778">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </form>
        </div>
      </div>
    </div>;
};